<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
  hideReplyTo: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  showContextMenuTrigger,
  openContextMenu,
  abreBloco,
  nomeNoBalao,
  corDoAutor,
} = useMessageContext();
const { t } = useI18n();

// Chevron ancorado no canto da bolha (mesmo padrão do chat interno, pedido
// do Victor 2026-07-15) — antes ficava inline junto do horário lá embaixo.
const chevronColorClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.PRIVATE)
    return 'text-n-amber-12/60 hover:text-n-amber-12 hover:bg-n-amber-12/10';
  if (
    variant.value === MESSAGE_VARIANTS.AGENT ||
    variant.value === MESSAGE_VARIANTS.BOT ||
    variant.value === MESSAGE_VARIANTS.TEMPLATE
  ) {
    return 'text-white/70 hover:text-white hover:bg-white/20';
  }
  return 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2';
});

// O véu atrás do chevron tem a cor da bolha que o hospeda.
const variantTom = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return 'privada';
  if (
    [
      MESSAGE_VARIANTS.AGENT,
      MESSAGE_VARIANTS.BOT,
      MESSAGE_VARIANTS.TEMPLATE,
    ].includes(variant.value)
  )
    return 'agente';
  return 'cliente';
});

// A cor das bolhas mora em CSS de peça (fim do arquivo), e não em utilitário:
// era `bg-[#2563eb]` cravado três vezes — um azul que não recebia o tema e
// que ninguém achava na hora de trocar a marca.
const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'msg-bolha--agente',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'msg-bolha--cliente',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'msg-bolha--agente',
  [MESSAGE_VARIANTS.TEMPLATE]: 'msg-bolha--agente',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

// A ponta tem a cor da bolha: só nas bolhas de cor conhecida.
const VARIANTES_COM_PONTA = [
  MESSAGE_VARIANTS.AGENT,
  MESSAGE_VARIANTS.BOT,
  MESSAGE_VARIANTS.TEMPLATE,
  MESSAGE_VARIANTS.USER,
  MESSAGE_VARIANTS.PRIVATE,
];

const orientationMap = {
  [ORIENTATION.LEFT]: 'msg-bolha left-bubble',
  [ORIENTATION.RIGHT]: 'msg-bolha right-bubble',
  [ORIENTATION.CENTER]: 'msg-bolha msg-bolha--centro',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
    if (abreBloco?.value && VARIANTES_COM_PONTA.includes(variant.value)) {
      classToApply.push('msg-bolha--ponta', `msg-ponta--${variantTom.value}`);
    }
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

// Victor pediu 2026-07-16: cada mensagem mostra seu próprio horário+status,
// mesmo agrupada com a próxima do mesmo remetente (antes só a última do
// grupo mostrava — comportamento padrão do Chatwoot, mas confuso pro caso de
// uso jurídico onde cada mensagem pode ter contexto/hora relevante).
const shouldShowMeta = computed(
  () => !props.hideMeta && variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});

const replyToImage = computed(() => {
  if (!inReplyTo) return null;

  const firstAttachment = inReplyTo.value.attachments?.[0];
  if (!firstAttachment) return null;

  const fileType = firstAttachment.fileType ?? firstAttachment.file_type;
  if (fileType !== 'image') return null;

  return firstAttachment.dataUrl ?? firstAttachment.data_url;
});
</script>

<template>
  <div
    class="text-sm relative group"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <!-- Chevron do menu de ações — ancorado no canto da bolha (mesmo padrão
         do chat interno, pedido do Victor 2026-07-15): antes ficava inline
         junto do horário lá embaixo, discreto demais / posição inconsistente
         entre bolha de texto e áudio. Fica aqui no Base.vue (não em
         MessageMeta) pra cobrir os dois tipos de bolha de uma vez só, já que
         Audio.vue também usa BaseBubble como wrapper externo. -->
    <!-- 🔴 SEMPRE NO CANTO DIREITO (Victor, 16/09/2026: "está no lugar
         errado no do cliente — deveria estar à direita, igual ao que o agente
         envia, e é o padrão do WhatsApp"). Ficava à esquerda na mensagem que
         chega: a mão aprendia um lugar para as nossas e outro para as do
         cliente.
         Entra deslizando de cima com um véu da cor da própria bolha atrás,
         para não pousar em cima do texto. -->
    <button
      v-if="showContextMenuTrigger && variant !== MESSAGE_VARIANTS.ACTIVITY"
      type="button"
      class="msg-menu-gatilho"
      :class="[chevronColorClass, `msg-menu-gatilho--${variantTom}`]"
      :aria-label="t('CONVERSATION.CONTEXT_MENU.OPEN')"
      @click.stop="openContextMenu($event)"
    >
      <Icon icon="i-ph-caret-down-bold" class="msg-menu-gatilho__ico" />
    </button>
    <p
      v-if="nomeNoBalao"
      class="msg-autor"
      :class="
        variantTom === 'cliente'
          ? `msg-autor--${corDoAutor}`
          : `msg-autor--${variantTom}`
      "
    >
      {{ nomeNoBalao }}
    </p>
    <div
      v-if="inReplyTo && !props.hideReplyTo"
      class="flex gap-2 p-2 -mx-1 mb-2 rounded-lg cursor-pointer shadow-sm border"
      :class="
        variant === MESSAGE_VARIANTS.AGENT ||
        variant === MESSAGE_VARIANTS.BOT ||
        variant === MESSAGE_VARIANTS.TEMPLATE
          ? 'bg-white/15 border-white/20'
          : 'bg-n-solid-2 border-n-weak'
      "
      @click="scrollToMessage"
    >
      <img
        v-if="replyToImage"
        :src="replyToImage"
        class="size-10 rounded-md object-cover flex-shrink-0"
      />
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2 text-n-slate-12"
      />
    </div>
    <slot />
    <MessageMeta
      v-if="!props.hideMeta && variant !== MESSAGE_VARIANTS.ACTIVITY"
      :show-timestamp="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        { 'msg-meta-foto': $attrs['data-bubble-name'] === 'image' },
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : variant === MESSAGE_VARIANTS.AGENT
            ? 'text-white/60'
            : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>

<style scoped>
/* ── Celular ─────────────────────────────────────────────────────────────────
   O gatilho do menu da mensagem vivia em `opacity-0`, aparecendo só no hover.
   Toque não tem hover: medido em 08/09/2026 numa conversa real, eram 15
   botões de 18x18 com opacidade 0 — invisíveis E inalcançáveis no celular.
   Contraria a regra 2.5 do padrão ("nada de função escondida atrás de hover",
   alvo mínimo de 32px).

   No celular ele aparece esmaecido (não rouba a atenção da mensagem, que é o
   conteúdo) e a área de toque cresce por ::after — sem mexer no tamanho do
   ícone nem no layout da bolha. Acima de 640px nada muda. */
/* ── A bolha ──────────────────────────────────────────────────────────────
   Raio de cartão (`--r-4`) com o canto do lado de quem falou FECHADO
   (`--r-1`): é o que diz de onde a fala saiu sem precisar de cauda. */
.msg-bolha {
  border-radius: var(--r-4);
}

.msg-bolha.left-bubble {
  border-end-start-radius: var(--r-1);
}

.msg-bolha.right-bubble {
  border-end-end-radius: var(--r-1);
}

.msg-bolha--centro {
  border-radius: var(--r-3);
}

/* ── A ponta da primeira bolha do bloco ────────────────────────────────────
   Como no WhatsApp: o canto de cima do lado de quem fala fica reto e um
   triângulo sai dele em direção ao rosto. A cor vem de `--msg-ponta` (e
   `--msg-ponta-aro`, na bolha com borda), definida por tom logo abaixo. */
.msg-bolha--ponta.left-bubble {
  border-start-start-radius: 0;
}

.msg-bolha--ponta.right-bubble {
  border-start-end-radius: 0;
}

.msg-bolha--ponta::before,
.msg-bolha--ponta::after {
  content: '';
  position: absolute;
  top: -1px;
  width: 9px;
  height: 13px;
  pointer-events: none;
}

.msg-bolha--ponta.left-bubble::before,
.msg-bolha--ponta.left-bubble::after {
  inset-inline-start: -9px;
  clip-path: polygon(0 0, 100% 0, 100% 100%);
}

.msg-bolha--ponta.right-bubble::before,
.msg-bolha--ponta.right-bubble::after {
  inset-inline-end: -9px;
  clip-path: polygon(0 0, 100% 0, 0 100%);
}

/* O aro (bolha com borda) por baixo, a cor da bolha por cima, 1px para
   dentro — a borda contorna a ponta sem emenda. */
.msg-bolha--ponta::before {
  background: var(--msg-ponta-aro, var(--msg-ponta));
}

.msg-bolha--ponta::after {
  top: 0;
  background: var(--msg-ponta);
}

.msg-bolha--ponta.left-bubble::after {
  inset-inline-start: -7px;
  width: 8px;
  height: 11px;
}

.msg-bolha--ponta.right-bubble::after {
  inset-inline-end: -7px;
  width: 8px;
  height: 11px;
}

.msg-ponta--agente {
  --msg-ponta: rgb(var(--blue-9));
}

.msg-ponta--cliente {
  --msg-ponta: rgb(var(--slate-3));
  --msg-ponta-aro: rgb(var(--slate-5));
}

.msg-ponta--privada {
  --msg-ponta: rgb(var(--solid-amber));
}

/* Na bolha de foto a moldura é fina: nome e hora ganham recuo próprio. */
[data-bubble-name='image'] > .msg-autor {
  padding: var(--esp-1) var(--esp-5) 0 var(--esp-2);
}

[data-bubble-name='image'] > .msg-meta-foto {
  margin-top: var(--esp-1);
  padding: 0 var(--esp-2) var(--esp-1);
}

/* ── O nome de quem fala, dentro da bolha ─────────────────────────────── */
.msg-autor {
  margin: 0 0 var(--esp-1);
  overflow: hidden;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  text-overflow: ellipsis;
  white-space: nowrap;
  /* O chevron do menu pousa no canto de cima: o nome não corre por baixo. */
  padding-inline-end: var(--esp-5);
}

.msg-autor--agente {
  color: rgb(255 255 255 / 85%);
}

.msg-autor--privada {
  color: rgb(var(--amber-12) / 80%);
}

.msg-autor--blue {
  color: rgb(var(--blue-11));
}

.msg-autor--teal {
  color: rgb(var(--teal-11));
}

.msg-autor--iris {
  color: rgb(var(--iris-11));
}

.msg-autor--violeta {
  color: rgb(var(--violet-11));
}

.msg-autor--ambar {
  color: rgb(var(--amber-11));
}

.msg-autor--ruby {
  color: rgb(var(--ruby-11));
}

/* A nossa: o azul da marca com um fio de profundidade (o degradê desce meio
   tom para o íris) e a sombra de CONTATO do padrão — uma camada só, em
   repouso. */
.msg-bolha--agente {
  background: linear-gradient(
    165deg,
    rgb(var(--blue-9)),
    color-mix(in srgb, rgb(var(--blue-9)) 88%, rgb(var(--iris-9)))
  );
  color: rgb(255 255 255);
  box-shadow: 0 1px 2px rgb(15 23 42 / 14%);
}

/* A do cliente: rebaixo cinza com aro, legível sobre a trama clara do fundo
   da conversa. */
.msg-bolha--cliente {
  border: 1px solid rgb(var(--slate-5));
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
  box-shadow: 0 1px 2px rgb(15 23 42 / 6%);
}

/* ── O chevron do menu da mensagem ─────────────────────────────────────── */
.msg-menu-gatilho {
  position: absolute;
  z-index: 10;
  top: var(--esp-1);
  inset-inline-end: var(--esp-1);
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: var(--r-2);
  cursor: pointer;
  opacity: 0;
  translate: 0 -4px;
  scale: 0.85;
  transition:
    opacity var(--mov-1) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva-pulo),
    background-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

/* O véu: um degradê da cor da bolha à esquerda do botão, para o chevron
   nunca pousar em cima de uma palavra — é o mesmo truque dos mensageiros. */
.msg-menu-gatilho::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: -2px -2px -2px -18px;
  border-radius: var(--r-3);
  background: linear-gradient(to left, var(--msg-veu) 60%, transparent);
  pointer-events: none;
}

/* 🔴 No áudio que CHEGA, o canto direito é do rosto de quem gravou (Victor,
   16/09/2026: "desce um negocinho branco aí em cima"). O véu, que tem a cor
   da bolha, pousava sobre o avatar escuro e virava uma mancha clara. Ali o
   chevron vai para a esquerda do rosto, sobre o topo vazio do player — o
   mesmo lugar em que os mensageiros o colocam. */
.left-bubble[data-bubble-name='audio'] > .msg-menu-gatilho {
  inset-inline-end: calc(38px + var(--esp-4));
}

.msg-menu-gatilho--agente {
  --msg-veu: rgb(var(--blue-9));
}

.msg-menu-gatilho--cliente {
  --msg-veu: rgb(var(--slate-3));
}

.msg-menu-gatilho--privada {
  --msg-veu: rgb(var(--amber-3));
}

.msg-menu-gatilho__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

/* Aparece no hover da BOLHA (não só do botão), e com o menu aberto por
   teclado também. */
.group:hover > .msg-menu-gatilho,
.msg-menu-gatilho:focus-visible {
  opacity: 1;
  translate: 0 0;
  scale: 1;
}

/* Em cima do próprio botão, a seta dá um passo para baixo — o convite para
   abrir, na direção em que o menu vai nascer. */
.msg-menu-gatilho:hover .msg-menu-gatilho__ico {
  translate: 0 2px;
}

.msg-menu-gatilho:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

@media (prefers-reduced-motion: reduce) {
  .msg-menu-gatilho,
  .msg-menu-gatilho__ico {
    transition: none;
    translate: none;
    scale: 1;
  }

  .msg-menu-gatilho:hover .msg-menu-gatilho__ico {
    translate: none;
  }
}

@media (max-width: 639px) {
  .msg-menu-gatilho {
    opacity: 0.45;
    translate: 0 0;
    scale: 1;
  }

  .msg-menu-gatilho::after {
    content: '';
    position: absolute;
    inset: -8px;
  }
}
</style>
